/* Quick-add panel for product cards (collections, search, featured rows).
 *
 * Replaced the size grid that sat on top of the card photo (Adam, 2026-10-23).
 * That grid flattened every variant into one row of equal boxes ("S / Carbon
 * Grey" x 24) inside a photo frame that clips, so long labels printed over each
 * other and big products were cut off. The panel opens like the bag does, shows
 * the product, groups the choices by option (Size, then Color, in the store's
 * order) and adds the one matching variant through the theme's existing
 * [data-quick-add] handler in an-cart.js.
 *
 * Each card carries its data in <script type="application/json" data-qs-data>
 * (snippets/an-quick-data.liquid):
 *   {url, title, image, priceHtml, options:[names], variants:[{id, o:[values], a:0|1, po:0|1}]}
 * priceHtml is the theme's own an-price markup (money filter output), never shopper input.
 * a = available, po = pre-order (those link to the product page, where the
 * pre-order terms box is).
 *
 * The option logic is pure and unit-tested in Node:
 *   04-technical/scripts/tests/an-quick-sheet.test.cjs
 */
(function (root) {
  function groups(data) {
    return (data.options || []).map(function (name, i) {
      var seen = {}, values = [];
      (data.variants || []).forEach(function (v) {
        var val = v.o[i];
        if (val != null && !seen[val]) { seen[val] = true; values.push(val); }
      });
      return { name: name, values: values };
    });
  }

  function autoSelect(data) {
    return groups(data).map(function (g) { return g.values.length === 1 ? g.values[0] : null; });
  }

  function matches(v, selected, skip) {
    for (var i = 0; i < selected.length; i++) {
      if (i === skip || selected[i] == null) continue;
      if (v.o[i] !== selected[i]) return false;
    }
    return true;
  }

  // 'ok' (can be bought or pre-ordered), 'soldout', or 'none' (no such combination)
  function valueState(data, selected, i, value) {
    var found = false;
    var vs = data.variants || [];
    for (var k = 0; k < vs.length; k++) {
      var v = vs[k];
      if (v.o[i] !== value || !matches(v, selected, i)) continue;
      found = true;
      if (v.a || v.po) return 'ok';
    }
    return found ? 'soldout' : 'none';
  }

  function resolve(data, selected) {
    for (var i = 0; i < selected.length; i++) if (selected[i] == null) return null;
    var vs = data.variants || [];
    for (var k = 0; k < vs.length; k++) if (matches(vs[k], selected, -1)) return vs[k];
    return null;
  }

  function article(word) { return /^[aeiou]/i.test(word) ? 'an' : 'a'; }

  // What the panel's main button does for the current choice.
  function action(data, selected) {
    for (var i = 0; i < selected.length; i++) {
      if (selected[i] == null) {
        var name = String((data.options || [])[i] || 'option').toLowerCase();
        var label = 'Choose ' + article(name) + ' ' + name;
        return { kind: 'choose', label: label.length <= 26 ? label : 'Choose an option' };
      }
    }
    var v = resolve(data, selected);
    if (!v) return { kind: 'soldout', label: 'Not available' };
    if (v.po) return { kind: 'preorder', label: 'Pre-order', href: data.url + '?variant=' + v.id };
    if (!v.a) return { kind: 'soldout', label: 'Sold out' };
    return { kind: 'add', label: 'Add to bag', id: v.id };
  }

  var api = { groups: groups, autoSelect: autoSelect, valueState: valueState, resolve: resolve, action: action };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.ANQuickSheet = api;

  // ---- UI (browser only) -------------------------------------------------
  if (typeof document === 'undefined') return;

  var sheet, panel, bodyEl, data, selected, opener;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function build() {
    sheet = document.createElement('div');
    sheet.className = 'an-qs';
    sheet.setAttribute('aria-hidden', 'true');
    sheet.innerHTML =
      '<div class="an-qs-overlay" data-qs-close></div>' +
      '<aside class="an-qs-panel" role="dialog" aria-modal="true" aria-labelledby="anQsTitle">' +
        '<header class="an-qs-head">' +
          '<button type="button" class="an-qs-x" data-qs-close aria-label="Close">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
          '</button>' +
        '</header>' +
        '<div class="an-qs-body"></div>' +
      '</aside>';
    document.body.appendChild(sheet);
    panel = sheet.querySelector('.an-qs-panel');
    bodyEl = sheet.querySelector('.an-qs-body');
  }

  function render() {
    var gs = groups(data);
    var html =
      '<div class="an-qs-prod">' +
        (data.image ? '<img src="' + esc(data.image) + '" alt="" width="96" height="120">' : '') +
        '<div><h2 class="an-qs-title" id="anQsTitle">' + esc(data.title) + '</h2>' +
        '<p class="an-qs-price price">' + (data.priceHtml || '') + '</p></div>' +
      '</div>';
    gs.forEach(function (g, i) {
      if (g.values.length < 2) return; // nothing to choose
      html += '<fieldset class="an-qs-opt"><legend>' + esc(g.name) +
        (selected[i] != null ? ': <b>' + esc(selected[i]) + '</b>' : '') + '</legend><div class="an-qs-vals">';
      g.values.forEach(function (val) {
        var st = valueState(data, selected, i, val);
        var on = selected[i] === val;
        html += '<button type="button" class="an-qs-val' + (st !== 'ok' ? ' is-out' : '') + '"' +
          ' data-qs-opt="' + i + '" data-qs-val="' + esc(val) + '" aria-pressed="' + on + '"' +
          (st === 'none' ? ' disabled' : '') +
          (st === 'soldout' ? ' aria-label="' + esc(val) + ', sold out"' : '') + '>' + esc(val) + '</button>';
      });
      html += '</div></fieldset>';
    });
    var a = action(data, selected);
    html += '<div class="an-qs-foot">';
    if (a.kind === 'add') {
      html += '<button type="button" class="an-qs-go" data-quick-add data-variant-id="' + a.id + '" data-qs-added>' + a.label + '</button>';
    } else if (a.kind === 'preorder') {
      html += '<a class="an-qs-go" href="' + esc(a.href) + '">' + a.label + '</a>';
    } else {
      html += '<button type="button" class="an-qs-go" disabled>' + a.label + '</button>';
    }
    html += '<a class="an-qs-more" href="' + esc(data.url) + '">See full details</a></div>';
    bodyEl.innerHTML = html;
  }

  function open(card, btn) {
    var node = card.querySelector('[data-qs-data]');
    if (!node) return false;
    try { data = JSON.parse(node.textContent); } catch (e) { return false; }
    if (!sheet) build();
    opener = btn;
    selected = autoSelect(data);
    render();
    sheet.classList.add('open');
    sheet.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var first = panel.querySelector('.an-qs-val:not([disabled])') || panel.querySelector('.an-qs-go');
    if (first) first.focus({ preventScroll: true });
    return true;
  }

  function close(keepFocus) {
    if (!sheet || !sheet.classList.contains('open')) return;
    sheet.classList.remove('open');
    sheet.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (!keepFocus && opener) opener.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    var qs = t.closest('[data-quick-sheet]');
    if (qs) {
      var card = qs.closest('.card');
      if (card && open(card, qs)) e.preventDefault();
      return;
    }
    if (!sheet || !sheet.classList.contains('open')) return;
    // the backdrop (or anything outside the panel) closes it
    if (t.closest('[data-qs-close]') || !t.closest('.an-qs-panel')) { e.preventDefault(); close(); return; }
    var val = t.closest('[data-qs-val]');
    if (val && !val.disabled) {
      var i = parseInt(val.getAttribute('data-qs-opt'), 10);
      var v = val.getAttribute('data-qs-val');
      selected[i] = selected[i] === v ? null : v;
      render();
      var again = panel.querySelector('[data-qs-opt="' + i + '"][data-qs-val="' + v.replace(/"/g, '\\"') + '"]');
      if (again) again.focus({ preventScroll: true });
      return;
    }
    // Add to bag: an-cart.js adds it and opens the bag; this panel steps aside.
    if (t.closest('[data-qs-added]')) close(true);
  });

  document.addEventListener('keydown', function (e) {
    if (!sheet || !sheet.classList.contains('open')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab') {
      var f = panel.querySelectorAll('button:not([disabled]), a[href]');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
})(typeof window !== 'undefined' ? window : this);
